import React, { useEffect, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { useOperatorAccountInfo } from "./OperatorAccountContext";
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Table,
  TableCell,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { updateOperatorAccountInfo, deleteOperatorAccountInfo } from "./utils";

const OperatorAccountForm = () => {
  const [open, setOpen] = useState(false);
  const [formErrors, setFormErrors] = useState({});
  const {
    operatorAccountInfo,
    setOperatorAccountInfo,
    selectedOperatorAccInfo,
    setSelectedOperatorAccInfo,
    setReload,
    agencies,
    setAgencies,
    selectedAgency,
    setSelectedAgency,
    authenticationChecked,
    setAuthenticationChecked,
  } = useOperatorAccountInfo();

  useEffect(() => {
    setOpen(operatorAccountInfo !== null || selectedOperatorAccInfo !== null);
  }, [operatorAccountInfo, setOperatorAccountInfo, setSelectedOperatorAccInfo]);

  const formValidate = (operatorAccountInfo) => {
    let newFormErrors = {};
    if (
      operatorAccountInfo.login === null ||
      operatorAccountInfo.login.trim() === ""
    ) {
      newFormErrors.login = "Please Enter Login Name";
    }
    if (operatorAccountInfo.password) {
      const password = operatorAccountInfo.password;

      if (password.length < 8) {
        newFormErrors.password = "Password must be at least 8 characters long";
      } else if (!/[A-Z]/.test(password)) {
        newFormErrors.password =
          "Password must contain at least one uppercase letter";
      } else if (!/\d/.test(password)) {
        newFormErrors.password = "Password must contain at least one digit";
      }
    }
    if (
      operatorAccountInfo.name === null ||
      operatorAccountInfo.name.trim() === ""
    ) {
      newFormErrors.name = "Please Enter Name";
    }
    if (
      operatorAccountInfo.email === null ||
      operatorAccountInfo.email.trim() === ""
    ) {
      newFormErrors.email = "Please Enter Email Address";
    }
    if (
      operatorAccountInfo.phoneNumber === null ||
      operatorAccountInfo.phoneNumber.trim() === ""
    ) {
      newFormErrors.phoneNumber = "Please enter Phone Number";
    }

    setFormErrors(newFormErrors);
    return Object.keys(newFormErrors).length === 0;
  };

  const updtOprAcc = (operatorAccInfo) => {
    if (formValidate(operatorAccInfo)) {
      updateOperatorAccountInfo(operatorAccInfo, setAuthenticationChecked).then(
        () => {
          setOperatorAccountInfo(null);
          setReload(true);
          setFormErrors({});
        },
      );
    }
  };

  const delOprAcc = (operatorAccInfo) => {
    deleteOperatorAccountInfo(operatorAccInfo, setAuthenticationChecked).then(
      () => {
        setOperatorAccountInfo(null);
        setReload(true);
      },
    );
  };

  const handleChange = (e) => {
    const name = e.target.name;
    const value = e.target.value;
    setOperatorAccountInfo((prev) => ({ ...prev, [name]: value }));
  };

  const handleAgencyChange = (e) => {
    const name = e.target.name;
    const value = e.target.value;
    const updatedAgency = agencies.find(
      (eachagency) => eachagency.name === value,
    );
    setOperatorAccountInfo((prev) => ({
      ...prev,
      [name]: value,
      ["agencyId"]: updatedAgency.id,
    }));
  };

  if (authenticationChecked !== null) {
    const isAccessDenied =
      authenticationChecked.includes("Access denied") ||
      authenticationChecked.includes("not authorized") ||
      authenticationChecked.includes("403") ||
      authenticationChecked.includes("FORBIDDEN");

    const isAuthRequired =
      authenticationChecked.includes("Authentication required") ||
      authenticationChecked.includes("Login expired") ||
      authenticationChecked.includes("session") ||
      authenticationChecked.includes("expired") ||
      authenticationChecked.includes("401") ||
      authenticationChecked.includes("UNAUTHORIZED");

    const wasLoggedIn = sessionStorage.getItem("isUser") === "true";
    const isAdminLoggedIn = sessionStorage.getItem("isAdmin") === "true";

    if (isAccessDenied && wasLoggedIn && !isAdminLoggedIn) {
      return (
        <Typography sx={{ textAlign: "center", m: 2 }} variant="h6">
          Access denied. You don't have the required permissions to view this
          page.
        </Typography>
      );
    } else if (isAccessDenied || (isAuthRequired && isAdminLoggedIn)) {
      return <Navigate to="/About/AdminLogin" replace />;
    } else if (isAuthRequired && wasLoggedIn) {
      return (
        <Typography sx={{ textAlign: "center", m: 2 }} variant="h6">
          Session expired.{" "}
          <Link to="/About/AdminLogin" className="hyper-links">
            <b>Click here to log back in</b>
          </Link>
        </Typography>
      );
    } else if (isAuthRequired) {
      return <Navigate to="/About/AdminLogin" replace />;
    } else {
      return (
        <Typography sx={{ textAlign: "center", m: 2 }} variant="h6">
          {authenticationChecked}{" "}
        </Typography>
      );
    }
  }

  return (
    operatorAccountInfo &&
    agencies && (
      <Card sx={{ borderRadius: 3, width: 500 }}>
        <CardHeader
          title={
            <font className="layer-title">
              {" "}
              Edit Operator Account Information{" "}
            </font>
          }
          align="center"
          className={"styledtablerow"}
        ></CardHeader>
        <CardContent
          sx={{
            paddingTop: 1,
            paddingLeft: 0,
            paddingRight: 0,
            overflow: "auto",
          }}
        >
          <Box
            component="form"
            size="small"
            sx={{
              "& .MuiTextField-root": { width: "35ch" },
              paddingTop: 3,
              align: "center",
            }}
          >
            <Grid container spacing={{ xs: 1 }} sx={{ paddingLeft: 15 }}>
              <Grid>
                <Table size="small">
                  <TableRow>
                    <TableCell className={"tablecellnoborder"}>
                      <TextField
                        required
                        size="small"
                        id="operator-account-login"
                        label="Login"
                        name="login"
                        value={operatorAccountInfo.login}
                        onChange={handleChange}
                        error={formErrors.login}
                        helperText={formErrors.login}
                      />
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell className={"tablecellnoborder"}>
                      <TextField
                        size="small"
                        id="operator-account-password"
                        label="Password"
                        type="password"
                        name="password"
                        value={operatorAccountInfo.password}
                        onChange={handleChange}
                        error={!!formErrors.password}
                        helperText={formErrors.password || ""}
                      />
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell className={"tablecellnoborder"}>
                      <TextField
                        required
                        size="small"
                        id="operator-account-name"
                        label="Name"
                        name="name"
                        value={operatorAccountInfo.name}
                        onChange={handleChange}
                        error={formErrors.name}
                        helperText={formErrors.name}
                      />
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell className={"tablecellnoborder"}>
                      <TextField
                        required
                        size="small"
                        id="operator-account-email"
                        label="Email"
                        name="email"
                        value={operatorAccountInfo.email}
                        onChange={handleChange}
                        error={formErrors.login}
                        helperText={formErrors.login}
                      />
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell className={"tablecellnoborder"}>
                      <TextField
                        required
                        size="small"
                        id="operator-account-phonenumber"
                        label="Phone Number"
                        name="phoneNumber"
                        value={operatorAccountInfo.phoneNumber}
                        onChange={handleChange}
                        error={formErrors.phoneNumber}
                        helperText={formErrors.phoneNumber}
                      />
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell className={"tablecellnoborder"}>
                      <TextField
                        size="small"
                        id="operator-account-agencyid"
                        label="Agency ID"
                        name="agencyId"
                        value={operatorAccountInfo.agencyId}
                        onChange={handleChange}
                        disabled
                      />
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell className={"tablecellnoborder"}>
                      <FormControl size="small" variant="outlined">
                        <InputLabel id="message-Priority-label">
                          {" "}
                          Agency{" "}
                        </InputLabel>
                        <Select
                          sx={{ minWidth: 150 }}
                          labelId="operator-account-Agency"
                          id="agency-selection"
                          value={operatorAccountInfo.agencyName}
                          label="Agency"
                          name="agencyName"
                          onChange={handleAgencyChange}
                        >
                          {agencies.map((eachagency) => (
                            <MenuItem
                              value={eachagency.name}
                              key={eachagency.id}
                            >
                              {eachagency.name}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </TableCell>
                  </TableRow>
                </Table>
              </Grid>
            </Grid>
          </Box>
          <Table>
            <TableRow>
              <TableCell align="right">
                <Button
                  name="Save"
                  label="Save"
                  variant="outlined"
                  size="small"
                  onClick={(e) => updtOprAcc(operatorAccountInfo)}
                >
                  Save
                </Button>
              </TableCell>
              <TableCell>
                <Button
                  name="Delete"
                  label="Delete"
                  variant="outlined"
                  size="small"
                  onClick={(e) => delOprAcc(operatorAccountInfo)}
                >
                  Delete
                </Button>
              </TableCell>
            </TableRow>
          </Table>
        </CardContent>
      </Card>
    )
  );
};

export default OperatorAccountForm;
